import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import GlassCard from './GlassCard';
import { TEXT_COLORS, BORDER_COLORS } from '../../utils/colors';
import { FONT_SIZES, SPACING, SCROLL_RESERVE } from '../../utils/responsive';

export interface MobileDataCardField {
  label: string;
  value: React.ReactNode;
  /** Занять обе колонки и разрешить перенос — для списков (иконки предметов) и длинного текста */
  fullWidth?: boolean;
}

export interface MobileDataCardProps {
  /** Слева от заголовка: ItemIcon, аватар, номер в рейтинге */
  icon?: React.ReactNode;
  /** Первая строка — то, по чему строку узнают (имя, логин, название предмета) */
  title: React.ReactNode;
  /** Вторая строка под заголовком (email, категория, id) */
  subtitle?: React.ReactNode;
  /** Справа от заголовка: StatusChip, роль, цена */
  badge?: React.ReactNode;
  /** Остальные колонки таблицы: подпись + значение, сеткой в две колонки */
  fields?: MobileDataCardField[];
  /** Кнопки действий (нижняя строка справа) */
  actions?: React.ReactNode;
  onClick?: () => void;
}

/**
 * Карточка-замена строки таблицы на мобильных (< sm).
 *
 * Единая замена для всех админ-таблиц: таблица не ужимается до читаемого вида, она заменяется
 * списком карточек (эталон — `RatingPage` / `SettingsPage` в личном кабинете, разбор в
 * `docs/responsive.md` §3.1). В кабинете таблиц было три и карточки писались по месту; в админке
 * их ~40, поэтому анатомия вынесена сюда — иначе карточки разъедутся между страницами.
 *
 * Правило наполнения: `title` + `subtitle` + `badge` — то, по чему строку ищут глазами;
 * `fields` — только те колонки, которые реально нужны в списке. Всё остальное живёт в детали/диалоге.
 */
const MobileDataCard: React.FC<MobileDataCardProps> = ({
  icon,
  title,
  subtitle,
  badge,
  fields,
  actions,
  onClick,
}) => (
  <GlassCard
    sx={{ p: SPACING.cardPadding, ...(onClick && { cursor: 'pointer' }) }}
    hoverEffect={false}
    onClick={onClick}
  >
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {/* Шапка: иконка | заголовок+подзаголовок | бейдж */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon && <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{icon}</Box>}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            component="div"
            sx={{
              fontSize: FONT_SIZES.cardTitle,
              fontWeight: 600,
              color: TEXT_COLORS.PRIMARY,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              '& a': { color: 'inherit', textDecoration: 'none' },
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              component="div"
              sx={{
                fontSize: FONT_SIZES.cardSubtitle,
                color: TEXT_COLORS.MUTED,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        {badge && <Box sx={{ flexShrink: 0 }}>{badge}</Box>}
      </Box>

      {/* Остальные колонки */}
      {fields && fields.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 0.75,
            pt: 0.75,
            borderTop: `1px solid ${BORDER_COLORS.DEFAULT}`,
          }}
        >
          {fields.map((field) => (
            <Box key={field.label} sx={{ minWidth: 0, ...(field.fullWidth && { gridColumn: '1 / -1' }) }}>
              <Typography
                component="div"
                sx={{
                  fontSize: FONT_SIZES.itemMuted,
                  color: TEXT_COLORS.CAPTION,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  lineHeight: 1.3,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {field.label}
              </Typography>
              <Typography
                component="div"
                sx={{
                  fontSize: FONT_SIZES.smallCaption,
                  color: TEXT_COLORS.LIGHT,
                  lineHeight: 1.3,
                  overflow: 'hidden',
                  ...(field.fullWidth
                    ? { whiteSpace: 'normal', overflowWrap: 'anywhere' }
                    : { textOverflow: 'ellipsis', whiteSpace: 'nowrap' }),
                  '& a': { color: 'inherit', textDecoration: 'none' },
                }}
              >
                {field.value}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Действия */}
      {actions && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 0.5,
            pt: 0.75,
            borderTop: `1px solid ${BORDER_COLORS.DEFAULT}`,
            '& .MuiIconButton-root': { padding: '4px' },
            '& .MuiSvgIcon-root': { fontSize: '1.1rem' },
          }}
        >
          {actions}
        </Box>
      )}
    </Box>
  </GlassCard>
);

export interface MobileCardListProps {
  /** callback-ref из `useInfiniteScroll` — вешается на область прокрутки SimpleBar */
  scrollRef?: React.Ref<HTMLDivElement>;
  /** показать спиннер докрутки под списком */
  busy?: boolean;
  /**
   * Блок над списком ВНУТРИ области прокрутки — например плитки сводки на страницах статистики.
   * Снаружи они не помещались: `GlassContainer` режет по своей высоте, и нижние плитки
   * вместе со списком становились недоступны.
   */
  header?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Область прокрутки для списка `MobileDataCard`. Заменяет `TableContainer` в мобильной ветке:
 * у таблицы прокрутка своя (иначе ломается `stickyHeader`), у списка карточек — `SimpleBar`.
 */
export const MobileCardList: React.FC<MobileCardListProps> = ({ scrollRef, busy, header, children }) => (
  <SimpleBar
    {...(scrollRef ? { scrollableNodeProps: { ref: scrollRef } } : {})}
    style={{ flex: 1, minHeight: 0, paddingTop: 8, paddingRight: SCROLL_RESERVE.xs }}
    autoHide={false}
  >
    {header && <Box sx={{ mb: 1.5 }}>{header}</Box>}
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pb: 1 }}>{children}</Box>
    {busy && (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} sx={{ color: 'rgba(139, 92, 246, 0.7)' }} />
      </Box>
    )}
  </SimpleBar>
);

export default MobileDataCard;
